<script>
import devData from './dev-data/weekly_sales_per_group_per_customer.json'
import api from '@/helpers/api'
import { sumBy, chain } from 'lodash'
import moment from 'moment'
import yearRangeMixin from './year-range.mixin'

export default {
  name: 'period-sales-per-group',
  mixins: [yearRangeMixin],
  props: {
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      selectedGroup: null,
      isLoading: false,
      total: 0,
      data: []
    }
  },
  watch: {
    startDate: 'onRangeChange',
    endDate: 'onRangeChange',
    year: 'onRangeChange'
  },
  mounted () {
    this.onRefresh()
  },
  methods: {
    onRangeChange () {
      this.selectedGroup = null
      this.onRefresh()
    },
    selectGroup (group) {
      this.selectedGroup = group
      this.onRefresh()
    },
    goBack () {
      this.selectedGroup = null
      this.onRefresh()
    },
    async onRefresh () {
      this.isLoading = true

      var result = []
      if (process.env.NODE_ENV !== 'production') {
        const start = moment(this.startDate)
        const end = moment(this.endDate)
        result = chain(devData)
          .filter(
            x =>
              moment(x.start_date).isSameOrAfter(start) &&
              moment(x.end_date).isSameOrBefore(end)
          )
          .value()
      } else {
        result = await api.stats.weeklySalesPerItemGroupPerCustomer({
          start_date: moment(this.startDate).format('MM/DD/YYYY'),
          end_date: moment(this.endDate).format('MM/DD/YYYY')
        })
      }

      if (this.selectedGroup !== null) {
        result = result.filter(
          x => x.top_parent_group_id === this.selectedGroup.id
        )
        this.data = chain(result)
          .groupBy(x => x.customer_account_id)
          .map((d, c) => ({
            customer: { ...d[0].customer, id: d[0].customer_account_id },
            value: sumBy(d, 'total_sales')
          }))
          .orderBy('value', 'desc')
          .value()
      } else {
        this.data = chain(result)
          .groupBy(x => x.top_parent_group_id)
          .map((d, c) => ({
            group: { ...d[0].top_item_group, id: d[0].top_parent_group_id },
            value: sumBy(d, 'total_sales')
          }))
          .orderBy('value', 'desc')
          .value()
      }

      this.total = sumBy(result, 'total_sales')

      this.isLoading = false
    }
  }
}
</script>
<template>
  <div class="portal-breakdown">
    <div class="portal-breakdown__header">
      <div class="portal-breakdown__subtitle">
        <template v-if="selectedGroup">
          <span>
            {{ $t('Sales') }} {{ selectedGroup.description }}
          </span>
          <button type="button" class="stats-tabs__btn" @click="goBack">
            {{ $t('GO BACK') }}
          </button>
        </template>
        <template v-else>
          {{ $t('Sales By Category') }}
        </template>
      </div>
    </div>

    <div v-if="selectedGroup === null" class="portal-data-table-wrap">
      <table class="portal-data-table">
        <thead>
          <tr>
            <th>{{ $t('Category') }}</th>
            <th class="text-right">{{ $t('Sales') }}</th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr>
            <td colspan="2" class="portal-data-table__loading">
              {{ $t('Loading...') }}
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!data.length">
          <tr>
            <td colspan="2" class="portal-data-table__empty">
              {{ $t('dashboard.activity.empty_table') }}
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="row in data"
            :key="row.group && row.group.id"
            class="is-clickable"
            @click="selectGroup(row.group)"
          >
            <td>
              <a
                href="#"
                class="portal-data-table__primary"
                @click.prevent="selectGroup(row.group)"
              >{{ row.group.description }}</a>
            </td>
            <td class="portal-data-table__value">
              {{ row.value | toCurrency }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="portal-data-table-wrap">
      <table class="portal-data-table">
        <thead>
          <tr>
            <th>{{ $t('Customer') }}</th>
            <th class="text-right">{{ $t('Sales') }}</th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr>
            <td colspan="2" class="portal-data-table__loading">
              {{ $t('Loading...') }}
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!data.length">
          <tr>
            <td colspan="2" class="portal-data-table__empty">
              {{ $t('dashboard.activity.empty_table') }}
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-for="row in data" :key="row.customer && row.customer.id">
            <td>
              <span class="portal-data-table__primary">{{
                row.customer.company_name
              }}</span>
            </td>
            <td class="portal-data-table__value">
              {{ row.value | toCurrency }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="portal-breakdown__footer">
      <span class="font-size-xs text-muted">
        {{ startDate | moment('DD/MM/YYYY') }} -
        {{ endDate | moment('DD/MM/YYYY') }}
      </span>
      <strong>{{ total | toCurrency }}</strong>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.portal-breakdown__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.portal-breakdown__subtitle {
  color: #94a3b8;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.portal-breakdown__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
</style>
